/**
 * phi-subject-select.js — Custom <phi-subject-select> element
 * Bordered combobox with floating label + chevron.
 * Keyboard: Enter/Space toggle, ArrowUp/Down, Home, End, Escape, type-ahead.
 */

(function () {
    'use strict';

    if (customElements.get('phi-subject-select')) return;

    class PhiSubjectSelect extends HTMLElement {

        connectedCallback() {
            if (this._connected) return;
            this._connected = true;

            this._trigger = this.querySelector('.phi-ssc__trigger');
            this._valueDisplay = this.querySelector('[data-phi-ssc-display]');
            this._label = this.querySelector('.phi-ssc__label');
            this._listbox = this.querySelector('.phi-ssc__listbox');
            this._hiddenInput = this.querySelector('[data-phi-ssc-value]');
            this._options = Array.from(this.querySelectorAll('.phi-ssc__option'));

            if (!this._trigger || !this._listbox || !this._options.length) return;

            this._isOpen = false;
            this._focusedIndex = -1;
            this._hasValue = false;
            this._typeBuffer = '';
            this._typeTimer = null;

            // Bound refs
            this._boundDocClick = this._onDocClick.bind(this);
            this._boundDocKey = this._onDocKeydown.bind(this);

            // Trigger
            this._trigger.addEventListener('click', this._onTriggerClick.bind(this));
            this._trigger.addEventListener('keydown', this._onTriggerKeydown.bind(this));

            // Options
            this._options.forEach((opt, idx) => {
                opt.addEventListener('mousedown', e => e.preventDefault());
                opt.addEventListener('click', () => this._select(opt));
                opt.addEventListener('mousemove', () => { if (this._focusedIndex !== idx) this._setFocused(idx); });
            });

            document.addEventListener('click', this._boundDocClick);
            document.addEventListener('keydown', this._boundDocKey);
        }

        disconnectedCallback() {
            document.removeEventListener('click', this._boundDocClick);
            document.removeEventListener('keydown', this._boundDocKey);
            this._connected = false;
        }

        // ── Open / close ──────────────────────────────────────────────────────

        _openDropdown() {
            this._isOpen = true;
            this._trigger.setAttribute('aria-expanded', 'true');
            this._trigger.classList.add('phi-ssc__trigger--open');
            this._listbox.classList.add('phi-ssc__listbox--open');
            this._listbox.setAttribute('aria-hidden', 'false');
            // Highlight selected or first
            const idx = this._options.findIndex(o => o.getAttribute('aria-selected') === 'true');
            this._setFocused(idx >= 0 ? idx : 0);
        }

        _closeDropdown() {
            this._isOpen = false;
            this._trigger.setAttribute('aria-expanded', 'false');
            this._trigger.classList.remove('phi-ssc__trigger--open');
            this._listbox.classList.remove('phi-ssc__listbox--open');
            this._listbox.setAttribute('aria-hidden', 'true');
            this._focusedIndex = -1;
            this._clearFocus();
        }

        // ── Select ────────────────────────────────────────────────────────────

        _select(optEl) {
            if (!optEl) return;
            this._options.forEach(o => {
                o.setAttribute('aria-selected', 'false');
                o.classList.remove('phi-ssc__option--selected');
            });
            optEl.setAttribute('aria-selected', 'true');
            optEl.classList.add('phi-ssc__option--selected');

            const label = optEl.dataset.label || optEl.textContent.trim();
            const value = optEl.dataset.value || label;

            if (this._valueDisplay) this._valueDisplay.textContent = label;
            if (this._hiddenInput) this._hiddenInput.value = value;

            // Float the label
            this._hasValue = true;
            this.classList.add('phi-ssc--has-value');
            this._trigger.setAttribute('aria-label', label);

            this._closeDropdown();
            this._trigger.focus();

            // Notify external listeners (e.g. blur-validation)
            this.dispatchEvent(new CustomEvent('phi-ssc:select', { bubbles: true, detail: { value, label } }));
        }

        // ── Focus management ─────────────────────────────────────────────────

        _setFocused(idx) {
            this._clearFocus();
            if (idx < 0 || idx >= this._options.length) return;
            this._focusedIndex = idx;
            this._options[idx].classList.add('phi-ssc__option--focused');
            requestAnimationFrame(() => this._options[idx].scrollIntoView({ block: 'nearest' }));
        }

        _clearFocus() {
            this._options.forEach(o => o.classList.remove('phi-ssc__option--focused'));
        }

        // ── Type-ahead ───────────────────────────────────────────────────────

        _typeAhead(char) {
            clearTimeout(this._typeTimer);
            this._typeBuffer += char.toLowerCase();
            // Find first matching option from current position onward
            const start = this._focusedIndex >= 0 ? this._focusedIndex + 1 : 0;
            let match = this._options.slice(start).findIndex(
                o => o.textContent.trim().toLowerCase().startsWith(this._typeBuffer)
            );
            if (match === -1) {
                // Wrap from beginning
                match = this._options.findIndex(
                    o => o.textContent.trim().toLowerCase().startsWith(this._typeBuffer)
                );
            } else {
                match += start;
            }
            if (match >= 0) this._setFocused(match);
            this._typeTimer = setTimeout(() => { this._typeBuffer = ''; }, 500);
        }

        // ── Event handlers ────────────────────────────────────────────────────

        _onTriggerClick(e) {
            e.stopPropagation();
            this._isOpen ? this._closeDropdown() : this._openDropdown();
        }

        _onTriggerKeydown(e) {
            switch (e.key) {
                case 'Enter':
                case ' ':
                    e.preventDefault();
                    e.stopPropagation();
                    if (this._isOpen && this._focusedIndex >= 0) {
                        this._select(this._options[this._focusedIndex]);
                    } else {
                        this._isOpen ? this._closeDropdown() : this._openDropdown();
                    }
                    break;
                case 'ArrowDown':
                    e.preventDefault();
                    if (!this._isOpen) { this._openDropdown(); break; }
                    this._setFocused(Math.min(this._focusedIndex + 1, this._options.length - 1));
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    if (!this._isOpen) { this._openDropdown(); break; }
                    this._setFocused(Math.max(this._focusedIndex - 1, 0));
                    break;
                case 'Home':
                    if (this._isOpen) { e.preventDefault(); this._setFocused(0); }
                    break;
                case 'End':
                    if (this._isOpen) { e.preventDefault(); this._setFocused(this._options.length - 1); }
                    break;
                case 'Escape':
                    if (this._isOpen) { e.preventDefault(); this._closeDropdown(); }
                    break;
                case 'Tab':
                    if (this._isOpen) this._closeDropdown();
                    break;
                default:
                    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
                        if (!this._isOpen) this._openDropdown();
                        this._typeAhead(e.key);
                    }
            }
        }

        _onDocKeydown(e) {
            if (!this._isOpen) return;
            if (e.key === 'Enter' && this._focusedIndex >= 0) {
                e.preventDefault();
                this._select(this._options[this._focusedIndex]);
            }
            if (e.key === 'Escape') {
                this._closeDropdown();
                this._trigger.focus();
            }
        }

        _onDocClick(e) {
            if (this._isOpen && !this.contains(e.target)) {
                this._closeDropdown();
            }
        }
    }

    customElements.define('phi-subject-select', PhiSubjectSelect);
})();
